import { TILE_HEIGHT, TILE_Z_OFFSET } from '../views/board/TileView';
import { EventScheduler } from '../../../utils/EventScheduler';
import type { BoardModel } from '../models/board/BoardModel';
import type { BoardHintsView } from '../views/board/BoardHintsView';
import type { GameAreaView } from '../GameAreaView';

export interface BoardHintControllerSettings {
  readonly delay: number;
  readonly minStacks: number;
}

export class BoardHintsController {
  private readonly view: BoardHintsView;

  private readonly scheduler = new EventScheduler();

  private hintCheckEventId = -1;

  constructor(
    private readonly gameAreaView: GameAreaView,
    private readonly boardModel: BoardModel,
    private readonly toggleStackHighlights: (enabled: boolean) => void,
    private readonly settings: BoardHintControllerSettings,
  ) {
    this.view = gameAreaView.hintsView;
  }

  public scheduleHintCheck(delay?: number): void {
    const {
      view,
      settings,
      boardModel,
      scheduler,
      gameAreaView: { boardView },
    } = this;

    this.unscheduleHintCheck();

    this.hintCheckEventId = scheduler.addEvent(() => {
      const stacks = boardModel.getStacks();
      const emptyStackCount = boardModel.getEmptyStackCount();

      if (emptyStackCount <= settings.minStacks) {
        // Of the remaining stacks filter to those that are fully obscured.
        const obscuredMinTiles = Math.floor(TILE_HEIGHT / TILE_Z_OFFSET);
        const obscuredStacks = stacks.filter(
          (stack) =>
            stack.isEmpty() && (boardModel.getStack(stack.x, stack.y + 1)?.getTileCount() ?? 0) > obscuredMinTiles,
        );

        // If every stack remaining is obscured we select them to be hinted.
        if (obscuredStacks.length === emptyStackCount) {
          obscuredStacks.forEach((stack) => {
            if (!stack.isEmpty()) return;
            const stackView = boardView.getStackView(stack.id);
            const stackBelowView = boardView.getStackViewByGridXY(stack.x, stack.y + 1);
            if (!stackView || !stackBelowView) return;
            view.addStackHint(stackView, stackBelowView);
            this.toggleStackHighlights(false);
          });
        }
      }
    }, delay ?? settings.delay);
  }

  public unscheduleHintCheck(): void {
    if (this.hintCheckEventId > -1) this.scheduler.removeEvent(this.hintCheckEventId);
    this.hintCheckEventId = -1;
  }

  public clearHints(): void {
    if (!this.view.hasActiveHints()) return;
    this.view.clearHints();
    this.toggleStackHighlights(true);
  }

  public update(deltaTime: number): void {
    this.scheduler.update(deltaTime);
    this.view.update(deltaTime);
  }
}
